import { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { AdminLayout } from '../../components/admin/AdminLayout';
import { Button } from '../../components/ui/button';
import { Loader2, Save, ExternalLink } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const LABELS = {
  nav: 'Menú de navegació', hero: 'Capçalera (Hero)', marquee: 'Cinta de text en moviment',
  intro: 'Introducció', method: 'El Mètode', letter: 'Carta personal', vip: 'Sessió VIP',
  video: 'Vídeo', about: 'Sobre Joan', footer: 'Peu de pàgina', whatsapp: 'Missatge de WhatsApp'
};

const setDeep = (obj, path, value) => {
  const clone = JSON.parse(JSON.stringify(obj));
  let ref = clone;
  for (let i = 0; i < path.length - 1; i++) ref = ref[path[i]];
  ref[path[path.length - 1]] = value;
  return clone;
};

const Field = ({ label, value, onChange }) => {
  const long = String(value).length > 70;
  return (
    <div className="mb-3">
      <label className="block text-xs font-medium text-gray-500 mb-1">{label}</label>
      {long ? (
        <textarea
          value={value}
          onChange={(e) => onChange(e.target.value)}
          rows={Math.min(8, Math.ceil(String(value).length / 90) + 1)}
          className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-[#1A4D2E]"
        />
      ) : (
        <input
          type="text"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-[#1A4D2E]"
        />
      )}
    </div>
  );
};

const NodeEditor = ({ node, path, onSet, label }) => renderNode(node, path, onSet, label);

function renderNode(node, path, onSet, label) {
  if (typeof node === 'string') {
    return <Field label={label} value={node} onChange={(v) => onSet(path, v)} />;
  }
  if (Array.isArray(node)) {
    if (node.every((x) => typeof x === 'string')) {
      return (
        <div className="mb-3">
          <label className="block text-xs font-medium text-gray-500 mb-1">{label} (una línia per element)</label>
          <textarea
            value={node.join('\n')}
            onChange={(e) => onSet(path, e.target.value.split('\n'))}
            rows={Math.min(14, node.length + 2)}
            className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-[#1A4D2E]"
          />
        </div>
      );
    }
    return (
      <div className="mb-3">
        <div className="text-xs font-medium text-gray-500 mb-2">{label}</div>
        <div className="space-y-2 pl-3 border-l-2 border-gray-200">
          {node.map((item, i) => (
            <div key={i}>{renderNode(item, [...path, i], onSet, `${label} #${i + 1}`)}</div>
          ))}
        </div>
      </div>
    );
  }
  if (node && typeof node === 'object') {
    return (
      <div className="mb-3 pl-3 border-l-2 border-gray-200">
        {Object.entries(node).map(([k, v]) => (
          <div key={k}>{renderNode(v, [...path, k], onSet, k)}</div>
        ))}
      </div>
    );
  }
  return null;
}

const AdminSoldeuLanding = () => {
  const { authFetch } = useAuth();
  const [data, setData] = useState(null);
  const [lang, setLang] = useState('ca');
  const [openSection, setOpenSection] = useState('hero');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetch(`${API_URL}/api/soldeu-landing`)
      .then((r) => r.json())
      .then(setData)
      .catch(() => toast.error('Error carregant el contingut'));
  }, []);

  const handleSet = (path, value) => setData((prev) => setDeep(prev, path, value));

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await authFetch(`${API_URL}/api/admin/soldeu-landing`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data)
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      toast.success('Landing guardada correctament');
    } catch (e) {
      toast.error(`Error guardant: ${e.message}. Si persisteix, torna a iniciar sessió.`);
    } finally {
      setSaving(false);
    }
  };

  if (!data) {
    return (
      <AdminLayout title="Landing Soldeu">
        <div className="flex justify-center py-16"><Loader2 className="w-8 h-8 animate-spin text-[#1A4D2E]" /></div>
      </AdminLayout>
    );
  }

  const t = data.t?.[lang] || {};

  return (
    <AdminLayout title="Landing Soldeu — Forat Zero">
      <div className="max-w-4xl">
        <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
          <a
            href="/Iniciacio-a-Soldeu"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 text-sm text-[#1A4D2E] underline"
            data-testid="soldeu-admin-preview-link"
          >
            <ExternalLink className="w-4 h-4" /> Veure la landing (/Iniciacio-a-Soldeu)
          </a>
          <Button onClick={handleSave} disabled={saving} className="bg-[#1A4D2E] hover:bg-[#153d24]" data-testid="soldeu-admin-save-btn">
            {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
            Guardar canvis
          </Button>
        </div>

        <div className="bg-white rounded-lg border border-gray-200 p-5 mb-6">
          <h3 className="text-sm font-semibold text-gray-700 mb-3">Configuració general</h3>
          <div className="grid md:grid-cols-2 gap-4">
            <Field label="Número de WhatsApp (sense +)" value={data.whatsapp || ''} onChange={(v) => handleSet(['whatsapp'], v)} />
            <Field label="ID del vídeo de YouTube" value={data.yt || ''} onChange={(v) => handleSet(['yt'], v)} />
          </div>
        </div>

        <div className="flex gap-2 mb-4 flex-wrap">
          {(data.langs || []).map((l) => (
            <button
              key={l.code}
              onClick={() => setLang(l.code)}
              className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${lang === l.code ? 'bg-[#1A4D2E] text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
              data-testid={`soldeu-admin-lang-${l.code}`}
            >
              {l.label}
            </button>
          ))}
        </div>

        <div className="space-y-3">
          {Object.entries(t).map(([sectionKey, sectionVal]) => (
            <div key={sectionKey} className="bg-white rounded-lg border border-gray-200">
              <button
                onClick={() => setOpenSection(openSection === sectionKey ? null : sectionKey)}
                className="w-full text-left px-5 py-3 font-medium text-sm text-gray-800 flex justify-between items-center"
                data-testid={`soldeu-admin-section-${sectionKey}`}
              >
                {LABELS[sectionKey] || sectionKey}
                <span className="text-gray-400">{openSection === sectionKey ? '−' : '+'}</span>
              </button>
              {openSection === sectionKey && (
                <div className="px-5 pb-5">
                  <NodeEditor node={sectionVal} path={['t', lang, sectionKey]} onSet={handleSet} label={sectionKey} />
                </div>
              )}
            </div>
          ))}
        </div>

        <div className="mt-6 flex justify-end">
          <Button onClick={handleSave} disabled={saving} className="bg-[#1A4D2E] hover:bg-[#153d24]">
            {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
            Guardar canvis
          </Button>
        </div>
      </div>
    </AdminLayout>
  );
};

export default AdminSoldeuLanding;
